import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { dayShort } from '../../lib/when';
import { markRead } from '../../lib/notify';
import useMediaQuery from '../../hooks/useMediaQuery';
import PageBar, { DESKTOP_QUERY } from '../../components/PageBar';
import { KIND_LABEL, KIND_TONE, asideFor, kindOf, noteFor } from '../../lib/tasks';
import PageLoading from '../../components/PageLoading';
import { pressable } from '../../lib/pressable';

/*
  Tasks: the things one person has asked for, and the things they have been told.

  Built to Group C's approved drawing on 5 September 2026.

  **There were THREE copies of this screen**, and the third one is why it
  matters. `DesktopTasks` in `RepDashboard.jsx` and `ManagerTasks` in
  `ManagerDashboard.jsx` were near-identical, and below 1024 the rep had a third
  version headed "Alerts" that showed the notifications and **left the whole
  "Raised by you" list out.** So a rep on a phone could not see one thing they
  had asked for: not an edit request, not a closure they had flagged, not a
  location they had picked up. There was nothing on the screen to say a section
  was missing.

  **"Assigned Tasks" was the older name and it was a lie**, renamed on Group C's
  finding of 31 August. Nothing under that heading had ever been assigned to
  anybody: every kind of to-do OnHand writes carries the id of the person who
  RAISED it. Giving somebody a job to do is a thing to build and it does not
  exist yet.
*/

// The colour of a notification's left edge, by what it is about. Its heading
// takes the matching word colour from ALERT_INK: the edge colours are too pale
// to read as words (theme phase 6).
const ALERT_INK = {
  'var(--success)': 'var(--success-ink)', 'var(--critical)': 'var(--critical-ink)',
  'var(--warning)': 'var(--warning-ink)', 'var(--info)': 'var(--info-ink)',
};
const ALERT_TONE = {
  count_approved:            'var(--success)',
  edit_approved:             'var(--success)',
  transfer_approved:         'var(--success)',
  edit_denied:               'var(--critical)',
  transfer_denied:           'var(--critical)',
  not_started:               'var(--text-secondary)',
  variance_flagged:          'var(--warning)',
  account_closed:            'var(--warning)',
  transfer_removed:          'var(--warning)',
  location_closure_flagged:  'var(--warning)',
  item_not_in_catalog:       'var(--info)',
};

function SectionHead({ label, right }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
      <span className="oh-ground-tag" style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 1.6, textTransform: 'uppercase', color: 'var(--text-secondary)' }}>{label}</span>
      <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
      <span className="oh-ground-tag" style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase', color: 'var(--text-secondary)' }}>{right}</span>
    </div>
  );
}

/*
  One thing this person asked for.

  **The chip sits beside the location down to 640 and above it below that**,
  which is what the drawings show: beside at 1440, 1194, 834 and 700, and above
  on a 390 phone. 640 is the same boundary a list uses to stop being a list, so
  the screen has one fewer number in it rather than two.
*/
function RaisedCard({ todo, wide }) {
  const kind = kindOf(todo);
  const tone = KIND_TONE[kind] || KIND_TONE.edit_request;
  const prefix = todo.warehouse?.region_prefix;
  const aside  = asideFor(todo);
  const chip = (
    <span style={{
      display: 'inline-block', fontSize: 9.5, fontWeight: 800, letterSpacing: 1,
      textTransform: 'uppercase', color: tone.fg, background: tone.bg,
      borderRadius: 'var(--oh-radius-chip)', padding: '4px 9px', whiteSpace: 'nowrap',
    }}>{KIND_LABEL[kind] || kind}</span>
  );
  return (
    <div className="card" style={{
      padding: '15px 18px', marginBottom: 10,
      display: 'flex', flexDirection: wide ? 'row' : 'column',
      alignItems: wide ? 'flex-start' : 'stretch', gap: wide ? 18 : 10,
    }}>
      <span style={{ flexShrink: 0, width: wide ? 132 : 'auto' }}>{chip}</span>
      <span style={{ minWidth: 0 }}>
        {/* Rule 16: the territory above the name, and the clean name. */}
        {prefix && (
          <span style={{ display: 'block', fontSize: 9, fontWeight: 800, letterSpacing: 1.4, color: 'var(--text-secondary)' }}>{prefix}</span>
        )}
        <span style={{ display: 'block', fontSize: 14.5, fontWeight: 800, color: 'var(--text-primary)' }}>
          {todo.warehouse?.display_name || todo.warehouse?.name || 'A location'}
        </span>
        <span style={{ display: 'block', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, marginTop: 3 }}>
          {noteFor(todo)}
        </span>
        {aside && (
          <span style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)', marginTop: 3 }}>{aside}</span>
        )}
        {/* **One quiet line saying whose it is, when, and where it has got to.**
            It replaces a gold "WAITING ON AN ADMIN" chip, which looked like a
            warning about something wrong rather than the ordinary state of a
            request somebody has just sent. */}
        <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 7 }}>
          {'Raised by you - ' + dayShort(todo.created_at) + ' - waiting on an admin'}
        </span>
      </span>
    </div>
  );
}

// One thing this person has been told.
function AlertCard({ alert, onRead }) {
  const tone = ALERT_TONE[alert.alert_type] || 'var(--info)';
  const unread = !alert.is_read;
  return (
    <div
      {...pressable(unread ? () => onRead(alert.id) : undefined)}
      className="card"
      style={{
        padding: '14px 18px', marginBottom: 10,
        borderLeft: '4px solid ' + tone,
        /* The unread tint laid OVER the card rather than instead of it: on its
           own the tint is see-through, and the words sat on the artwork. */
        background: unread ? 'linear-gradient(var(--hover), var(--hover)), var(--surface-card)' : 'var(--surface-card)',
        cursor: unread ? 'pointer' : 'default',
      }}
      title={unread ? 'Tap to mark this read' : undefined}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 9 }}>
        {/* **A dot, not a word.** An unread notification is marked the way the
            drawing marks it: a small filled dot and a tinted card. */}
        {unread && <span style={{ width: 6, height: 6, borderRadius: '50%', background: tone, flexShrink: 0 }} />}
        <span style={{ fontSize: 14, fontWeight: 800, color: ALERT_INK[tone] || tone }}>
          {alert.title || (alert.alert_type || '').replace(/_/g, ' ')}
        </span>
      </div>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, marginTop: 4, marginLeft: unread ? 15 : 0 }}>
        {alert.message}
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 6, marginLeft: unread ? 15 : 0 }}>
        {dayShort(alert.created_at)}
      </div>
    </div>
  );
}

export default function Tasks({ profile }) {
  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  const chipBeside = useMediaQuery('(min-width: 640px)');
  const [todos, setTodos]     = useState([]);
  const [alerts, setAlerts]   = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profile?.id) { setLoading(false); return; }
    let live = true;
    Promise.all([
      /*
        **The location comes from a join, not from the title.** Every open to-do
        carries a `warehouse_id`, and reading it properly is what lets the card
        print the territory once and the clean name once.
      */
      supabase.from('todos')
        .select('id, todo_type, description, metadata, created_at, warehouse:warehouses(id, name, display_name, region_prefix)')
        .eq('rep_id', profile.id).eq('is_complete', false)
        .order('created_at', { ascending: false }),
      supabase.from('alerts').select('*')
        .eq('rep_id', profile.id)
        .order('created_at', { ascending: false })
        .limit(30),
    ]).then(([{ data: todoData }, { data: alertData }]) => {
      if (!live) return;
      setTodos(todoData || []);
      setAlerts(alertData || []);
      setLoading(false);
    });
    return () => { live = false; };
  }, [profile]);

  const unread = alerts.filter(a => !a.is_read).length;

  /*
    **A save is proved by reading the row back, never by the absence of an
    error.** `markRead` returns false when the database changed no rows, which
    is what happened for months: marking a notification read had no rule
    permitting it, so the database quietly changed nothing and answered "fine",
    and the screen ticked it off until the page was reloaded.
  */
  async function readOne(id) {
    if (await markRead(id)) {
      setAlerts(prev => prev.map(a => a.id === id ? { ...a, is_read: true } : a));
    }
  }
  async function readAll() {
    const ids = alerts.filter(a => !a.is_read).map(a => a.id);
    if (ids.length === 0) return;
    if (await markRead(ids)) {
      setAlerts(prev => prev.map(a => ({ ...a, is_read: true })));
    }
  }

  if (loading) return <PageLoading rows={6} />;

  const raised = (
    <>
      <SectionHead label="Raised by you" right={todos.length} />
      {/*
        **One line saying what this list is**, which the drawing gives its own
        tinted band. It is here because the older heading said "Assigned Tasks"
        and a person reading "no tasks assigned to you" concluded their requests
        had gone nowhere.
      */}
      <div style={{
        display: 'flex', alignItems: 'flex-start', gap: 10,
        background: 'var(--warning-bg)', color: 'var(--warning-ink)',
        borderRadius: 'var(--oh-radius-control)', padding: '11px 14px', marginBottom: 12,
        fontSize: 12.5, lineHeight: 1.5, fontWeight: 600,
      }}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0, marginTop: 1 }}>
          <circle cx="12" cy="8" r="3.5" /><path d="M4.5 21a7.5 7.5 0 0 1 15 0" />
        </svg>
        <span>Every one of these is something you asked for. Nothing here has been given to you.</span>
      </div>
      {todos.length === 0 ? (
        <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
          You have not asked for anything that is still open.
        </div>
      ) : todos.map(t => <RaisedCard key={t.id} todo={t} wide={chipBeside} />)}
    </>
  );

  const told = (
    <>
      <SectionHead label="Notifications" right={unread + ' unread'} />
      {alerts.length === 0 ? (
        <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
          Nothing has happened to your counts yet.
        </div>
      ) : alerts.map(a => <AlertCard key={a.id} alert={a} onRead={readOne} />)}
    </>
  );

  return (
    <>
      <PageBar
        title="Tasks"
        actions={unread > 0
          ? <button className="btn btn-outline" onClick={readAll}>Mark all read</button>
          : null}
      />
      {/* **Two columns on a desk, one below 1024**, which is what the drawings
          show: side by side at 1440 and 1194, stacked at 834 and narrower. */}
      {isDesktop ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 26, alignItems: 'start' }}>
          <div>{raised}</div>
          <div>{told}</div>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 22 }}>{raised}</div>
          {told}
        </>
      )}
    </>
  );
}
